import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { X } from "lucide-react";

const KEY = "wylo_cookie_consent";

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    const existing = localStorage.getItem(KEY);
    if (!existing) setShow(true);
  }, []);

  if (!show) return null;

  const save = (override) => {
    const final = override || prefs;
    localStorage.setItem(KEY, JSON.stringify(final));
    setShow(false);
  };

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-20 inset-x-4 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-md z-[60] bg-white border border-[#E8ECF2] rounded-xl shadow-[0_20px_60px_rgba(26,58,92,0.15)] p-6"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-display text-lg font-bold text-[#1A1A2E]">
            Cookie preferences
          </h3>
          <p className="text-sm text-[#555568] mt-1">
            We use cookies to keep WYLO running. You choose what's optional. Compliant with GDPR & ePrivacy.
          </p>
        </div>
        <button onClick={() => setShow(false)} className="text-[#555568] hover:text-[#1A1A2E]" aria-label="Close">
          <X size={18} />
        </button>
      </div>

      {open && (
        <div className="space-y-3 mb-4 border-t border-[#E8ECF2] pt-4">
          <Row label="Necessary" desc="Required for the site to function. Cannot be disabled." checked locked />
          <Row
            label="Analytics"
            desc="Anonymised usage to improve WYLO."
            checked={prefs.analytics}
            onChange={(v) => setPrefs({ ...prefs, analytics: v })}
            testid="cookie-analytics"
          />
          <Row
            label="Marketing"
            desc="Used to tailor product communications."
            checked={prefs.marketing}
            onChange={(v) => setPrefs({ ...prefs, marketing: v })}
            testid="cookie-marketing"
          />
          <Row
            label="Functional"
            desc="Remembers preferences such as theme or sidebar state."
            checked={prefs.functional}
            onChange={(v) => setPrefs({ ...prefs, functional: v })}
            testid="cookie-functional"
          />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          className="flex-1 min-w-[120px]"
          onClick={() => setOpen(!open)}
          data-testid="cookie-customise-btn"
        >
          {open ? "Hide options" : "Customise"}
        </Button>
        <Button
          variant="outline"
          className="flex-1 min-w-[120px]"
          onClick={() => save({ necessary: true, analytics: false, marketing: false, functional: false })}
          data-testid="cookie-reject-btn"
        >
          Reject all
        </Button>
        <Button
          className="flex-1 min-w-[120px] bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white"
          onClick={() => save({ necessary: true, analytics: true, marketing: true, functional: true })}
          data-testid="cookie-accept-btn"
        >
          Accept all
        </Button>
      </div>
    </div>
  );
}

function Row({ label, desc, checked, onChange, locked, testid }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm font-semibold text-[#1A1A2E]">{label}</p>
        <p className="text-xs text-[#555568]">{desc}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        disabled={locked}
        data-testid={testid}
      />
    </div>
  );
}
